import { Box, Text } from "@primer/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../hooks/useAppDispatch";
import { rootActions } from "../state/actions/rootActions";
import { colors } from "../theme/tokens";

/**
 * Countdown phiên đăng nhập — clone Old Register.js `timeRemain()`.
 *
 * Đọc `expires_at` từ localStorage (set lúc LOGIN_SUCCESS / RESTORE_SESSION).
 * Tick mỗi 1s. Khi distance <= 0 → dispatch LOGOUT + navigate /login.
 *
 * Format compact cho header:
 *   - Còn ngày: "2d 03:45:12"
 *   - Còn giờ: "03:45:12"
 *   - Dưới 1 giờ: "45:12"
 *
 * Color escalation:
 *   - > 5 phút: text mặc định (#333)
 *   - 1–5 phút: warning (vàng)
 *   - < 1 phút: danger (đỏ) + animation pulse
 */
const formatRemain = (totalSeconds: number): string => {
    if (totalSeconds <= 0) return "00:00";
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    return `${pad(minutes)}:${pad(seconds)}`;
};

interface ISessionTimerProps {
    /** true = hiển thị gọn (chỉ số đếm, hiện cả trên mobile) để nhúng vào ô thống kê. */
    inline?: boolean;
}

export const SessionTimer = ({ inline = false }: ISessionTimerProps = {}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [remainSec, setRemainSec] = useState<number | null>(null);

    useEffect(() => {
        const raw = localStorage.getItem("expires_at");
        if (!raw) return;
        const expiresAt = new Date(raw).getTime();
        if (isNaN(expiresAt)) return;

        // Stagger logout chống thundering herd 50k SV cùng login lúc mở cổng → 60 phút sau token
        // expire đồng loạt → 50k navigate /login + POST /api/auth/login storm tier-2.
        // Mỗi tab random delay 0-180s → trải đều logout pulse trong 3 phút.
        // Lưu ý: chỉ giúp với SV idle. SV active vẫn bị axios 401 redirect ngay (apiClient.ts handleError),
        // nhưng phần lớn SV đã hoàn tất đăng ký + để tab mở → idle case là chính.
        const logoutStaggerMs = Math.floor(Math.random() * 180_000);

        let logoutScheduled = false;
        let logoutTimerId: ReturnType<typeof setTimeout> | null = null;
        const tick = () => {
            const now = Date.now();
            const remain = Math.floor((expiresAt - now) / 1000);
            setRemainSec(remain);
            if (remain <= 0 && !logoutScheduled) {
                // Guard logoutScheduled: tick còn chạy sau khi remain âm.
                logoutScheduled = true;
                logoutTimerId = setTimeout(() => {
                    dispatch(rootActions.auth.LOGOUT());
                    navigate("/login", { replace: true });
                }, logoutStaggerMs);
            }
        };

        tick();  // tick ngay lần đầu, không đợi 1s
        const intervalId = setInterval(tick, 1000);
        return () => {
            clearInterval(intervalId);
            if (logoutTimerId !== null) clearTimeout(logoutTimerId);
        };
    }, [dispatch, navigate]);

    if (remainSec === null) return null;

    const isCritical = remainSec < 60;
    const isWarning = remainSec < 300;
    const textColor = isCritical ? colors.danger : isWarning ? "#b88500" : "#333";

    // Biến thể gọn cho ô thống kê mobile — chỉ số đếm, không bị ẩn ở màn xs.
    if (inline) {
        return (
            <Text
                sx={{
                    fontWeight: 700,
                    color: textColor,
                    fontVariantNumeric: "tabular-nums",
                    animation: isCritical ? "sessionTimerPulse 1s ease-in-out infinite" : undefined,
                    "@keyframes sessionTimerPulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.5 },
                    },
                }}
            >
                {formatRemain(remainSec)}
            </Text>
        );
    }

    return (
        <Box
            sx={{
                display: ["none", "flex"],
                alignItems: "center",
                gap: 1,
                fontSize: 1,
                fontWeight: 600,
                color: textColor,
                // Tabular-nums giữ width đều khi số đổi → không nhấp nháy layout
                fontVariantNumeric: "tabular-nums",
                // Pulse animation khi critical
                animation: isCritical ? "sessionTimerPulse 1s ease-in-out infinite" : undefined,
                "@keyframes sessionTimerPulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.5 },
                },
            }}
            title="Phiên đăng ký còn lại"
        >
            <i className="far fa-clock" />
            <Text>{formatRemain(remainSec)}</Text>
        </Box>
    );
};
